import { useEffect, useMemo, useState } from "react";
import { Button, Drawer, message } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import type { BidAssociation, DsmScopeItem } from "design-scope";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { upsertBidAssociations } from "../shared/bulk-link-bid-package-merge";
import { toBulkLinkBidPackageUpdateInstance } from "../shared/design-scope-create-mappers";
import { DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE } from "../shared/design-scope-messages";
import ScopeBidPackagesCardsEditor, {
  pendingBidAssociations
} from "./scope-bid-packages-cards-editor";
import BulkSelectionViewPanel from "./bulk-selection-view-panel";
import type { ProjectEntities } from "../hooks/use-project-entities";
import "./scope-item.scss";

export type BulkLinkBidPackageDrawerProps = {
  open: boolean;
  selectedRows: DsmScopeItem[];
  entities: ProjectEntities;
  projectToken: string;
  onClose: () => void;
  onLinked: () => void | Promise<void>;
};

export default function BulkLinkBidPackageDrawer(
  props: BulkLinkBidPackageDrawerProps
) {
  const { open, selectedRows, entities, projectToken, onClose, onLinked } =
    props;

  const [bids, setBids] = useState<BidAssociation[]>([]);
  const [inProgress, setInProgress] = useState(false);

  const selectionKey = selectedRows.map((r) => r.id).join(",");

  useEffect(() => {
    if (!open) return;
    if (selectedRows.length === 0) {
      onClose();
      return;
    }
    setBids([{ bidPackageId: "", role: "", primary: true }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionKey covers row ids
  }, [open, selectionKey, onClose]);

  const bpOptions = useMemo(
    () =>
      entities.bidPackages.map((b) => ({
        value: b.id,
        label: b.label
      })),
    [entities.bidPackages]
  );

  const pending = pendingBidAssociations(bids);
  const canLink = pending.length > 0 && !entities.loading;

  const onLink = async () => {
    if (!canLink || inProgress) return;
    if (!projectToken) {
      message.error("Project auth is not ready. Please try again.");
      return;
    }
    setInProgress(true);
    try {
      const feature_instances = selectedRows.map((row) =>
        toBulkLinkBidPackageUpdateInstance(
          row.id,
          row.commitments,
          upsertBidAssociations(row, pending)
        )
      );
      const response = await updateDesignScopeInstances(
        {
          feature_type: "DESIGN_SCOPE",
          feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
          feature_instances
        },
        projectToken
      );
      if (!response.success) {
        message.error(response.errorMsg || "Unable to link Bid Packages.");
        return;
      }
      message.success(DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE);
      await onLinked();
    } catch (err) {
      console.error(err);
      message.error("Unable to link Bid Packages.");
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (!inProgress) onClose();
      }}
      size={560}
      closable={false}
      mask={{ closable: false }}
      destroyOnHidden
      className="dsm-edit"
      title={
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            data-testid="scope-bulk-link-bp-back"
            className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-ciq-primary-900"
            onClick={() => {
              if (!inProgress) onClose();
            }}
          >
            <ArrowLeftOutlined />
          </button>
          <span className="text-base font-medium text-black text-opacity-90">
            Bulk Link to Bid Package
          </span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            disabled={inProgress}
            data-testid="scope-bulk-link-bp-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canLink || inProgress}
            loading={inProgress}
            onClick={() => void onLink()}
            data-testid="scope-bulk-link-bp-submit"
          >
            Link
          </Button>
        </div>
      }
    >
      <div data-testid="scope-bulk-link-bp-drawer">
        <div className="mb-4 flex items-start gap-2 text-sm text-black text-opacity-50">
          <InfoCircleOutlined className="mt-0.5 shrink-0" />
          <span>
            All selected Design Scope items will be linked to the added Bid
            Packages.
          </span>
        </div>

        <BulkSelectionViewPanel
          items={selectedRows}
          data-testid="scope-bulk-link-bp"
        />

        <ScopeBidPackagesCardsEditor
          bids={bids}
          onBidsChange={setBids}
          bpOptions={bpOptions}
          sectionTitle="Link Bid Packages"
          addTestId="scope-bulk-link-bp-bid-add"
        />
      </div>
    </Drawer>
  );
}
